import { defineStore } from 'pinia'
import _ from 'lodash'

import { resourceApi, institutionApi } from './common/api'
import { rules, defaultItemsPerPage } from './common/commonFunctions'
import { licenseLines } from '@/utils/licenseCopy'

const licenseApi = resourceApi.extend('institutionLicense')

export const useLicense = defineStore('license', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['itemsPerPage', 'cachedStatusSelection', 'selectedStatuses'],
      storage: localStorage
    }
  ],

  state() {
    return {
      rules: rules.institutionLicense,
      appModes: [
        {
          title: 'Student',
          value: 'student'
        },
        {
          title: 'Instructor',
          value: 'instructor'
        },
        {
          title: 'Internal',
          value: 'internal'
        },
        {
          title: 'Demo',
          value: 'demo'
        },
        {
          title: 'Developer',
          value: 'developer'
        },
        {
          title: 'QA',
          value: 'qa'
        }
      ],
      selectedStatuses: null,
      cachedStatusSelection: null,
      tableData: [],
      isLoadingActiveLicenses: false,
      errorActiveLicenses: null,
      statusActiveLicenses: null,
      isLoadingArchivedLicenses: false,
      errorArchivedLicenses: null,
      statusArchivedLicenses: null,
      isLoadingArchivingLicenses: false,
      errorArchivingLicenses: null,
      statusArchivingLicenses: null,
      isLoadingRestoringLicenses: false,
      errorRestoringLicenses: null,
      statusRestoringLicenses: null,
      isLoadingEditingLicense: false,
      errorEditingLicense: null,
      statusEditingLicense: null,
      isLoadingCreatingLicense: false,
      errorCreatingLicense: null,
      statusCreatingLicense: null,
      isActionMultiple: null,
      activeLicenses: null,
      archivedLicenses: null,
      fetchLicensesFrequency: 300000,
      fetchActiveLicensesInterval: null,
      fetchArchivedLicensesInterval: null,
      editLicense: null,
      selectedLicenseIds: null,
      itemsPerPage: defaultItemsPerPage
    }
  },

  getters: {
    getAppModes: ({ appModes }) => {
      return appModes
    },
    getRules: ({ rules }) => {
      return rules
    },
    getActiveLicenses: ({ activeLicenses }) => {
      return activeLicenses
    },
    getSelectedStatuses: ({ selectedStatuses }) => {
      return selectedStatuses
    },
    getCachedStatusSelection: ({ cachedStatusSelection }) => {
      return cachedStatusSelection
    },
    isArchivedStatus: ({ selectedStatuses }) =>
      selectedStatuses.includes('archived'),
    getStatusSummary: ({ selectedStatuses }) => {
      if (!selectedStatuses?.length) return '(None)'
      if (selectedStatuses.includes('archived')) return '(Archived)'
      return `(${selectedStatuses.length} selected)`
    },
    getData: ({ activeLicenses, archivedLicenses, itemsPerPage }) => {
      const headers = [
        {
          title: 'Institution',
          key: 'institution',
          value: 'institution'
        },
        {
          title: 'License Code',
          key: 'licenseCode',
          value: 'licenseCode',
          minWidth: '5rem',
          maxWidth: '7rem',
          width: '5rem'
        },
        {
          title: 'Valid From',
          key: 'validFrom',
          value: 'validFrom',
          minWidth: '8rem',
          maxWidth: '8rem',
          width: '8rem'
        },
        {
          title: 'Valid To',
          key: 'validTo',
          value: 'validTo',
          minWidth: '8rem',
          maxWidth: '8rem',
          width: '8rem'
        },
        {
          title: 'Provisioned Licenses',
          key: 'maxDeviceLicenses',
          value: 'maxDeviceLicenses',
          minWidth: '5.6rem',
          maxWidth: '7.6rem',
          width: '5.6rem'
        },
        {
          title: 'Remaining Licenses',
          key: 'remainingLicenses',
          value: 'remainingLicenses',
          minWidth: '5.2rem',
          maxWidth: '7.2rem',
          width: '5.2rem'
        },
        {
          title: 'Users',
          key: 'userAllowance',
          sortable: false,
          minWidth: '16rem',
          width: '16rem',
          value: (item) => licenseLines(item).join(' ')
        },
        {
          title: 'Department',
          key: 'department',
          value: 'department'
        },
        {
          title: 'Application Mode',
          key: 'appMode',
          value: 'appMode',
          minWidth: '7rem',
          maxWidth: '8rem',
          width: '7rem'
        },
        {
          title: 'Status',
          key: 'status',
          value: 'status'
        },
        {
          key: 'actions',
          sortable: false
        }
      ]
      const filterKeys = _.map(headers.slice(0, -1), (h) => h.key)
      const customFilters = {
        institution: (value, search) => {
          if (!search) return true
          return (value?.name || '')
            .toLowerCase()
            .includes(search.toLowerCase())
        }
      }
      const customSort = {
        institution: (currentInst, nextInst) => {
          const currentName = currentInst?.name?.toLowerCase()
          const nextName = nextInst?.name?.toLowerCase()
          if (currentName == null && nextName != null) return -1
          if (currentName != null && nextName == null) return 1
          if (currentName != nextName)
            return currentName.localeCompare(nextName)
          else {
            const currentCountry = currentInst?.countryCode?.toLowerCase()
            const nextCountry = nextInst?.countryCode?.toLowerCase()
            if (currentCountry == null && nextCountry != null) return -1
            if (currentCountry != null && nextCountry == null) return 1
            return currentCountry.localeCompare(nextCountry)
          }
        }
      }
      let statuses = {
        active: {
          active: {
            title: 'Active',
            value: 'active',
            icon: 'mdi-check-decagram',
            color: 'success'
          },
          soonToExpire: {
            title: 'Soon To Expire',
            value: 'soonToExpire',
            icon: 'mdi-clock-alert',
            color: 'warning'
          },
          expired: {
            title: 'Expired',
            value: 'expired',
            icon: 'mdi-alert',
            color: 'error'
          }
        }
      }
      if (archivedLicenses != null && archivedLicenses.length > 0)
        statuses.archived = {
          title: 'Archived',
          value: 'archived',
          icon: 'mdi-package-down',
          color: 'secondary'
        }
      let returnValue = {
        headers,
        statuses,
        filterKeys,
        customFilters,
        customSort,
        itemsPerPage
      }
      if (activeLicenses == null && archivedLicenses == null)
        return { ...returnValue, data: [], countActive: 0, countArchived: 0 }
      const isExpired = (dateStr) => new Date(dateStr) < new Date()
      const isSoonToExpire = (dateStr) => {
        const today = new Date().toISOString().split('T')[0]
        const target = new Date(dateStr).toISOString().split('T')[0]
        const daysDiff =
          (new Date(target) - new Date(today)) / (1000 * 3600 * 24)
        return daysDiff >= 0 && daysDiff <= 30
      }
      let _activeLicenses = []
      let _archivedLicenses = []
      if (activeLicenses != null)
        _activeLicenses = activeLicenses.map((l, i) => {
          l.index = i
          l.status = isExpired(l.validTo)
            ? 'expired'
            : isSoonToExpire(l.validTo)
              ? 'soonToExpire'
              : 'active'
          return l
        })
      if (archivedLicenses != null)
        _archivedLicenses = archivedLicenses.map((l, i) => {
          l.index = i
          l.status = 'archived'
          return l
        })
      const data = [..._activeLicenses, ..._archivedLicenses]
      return {
        ...returnValue,
        data,
        countActive: activeLicenses?.length ?? 0,
        countArchived: archivedLicenses?.length ?? 0
      }
    },
    getTableData: ({ tableData }) => tableData,
    getEditLicense: ({ editLicense }) => {
      return editLicense
    },
    getFetchLicensesFrequency: ({ fetchLicensesFrequency }) => {
      return fetchLicensesFrequency
    },
    getFetchActiveLicensesInterval: ({ fetchActiveLicensesInterval }) => {
      return fetchActiveLicensesInterval
    },
    getFetchArchivedLicensesInterval: ({ fetchArchivedLicensesInterval }) => {
      return fetchArchivedLicensesInterval
    },
    getIsLoadingActiveLicenses: ({ isLoadingActiveLicenses }) => {
      return isLoadingActiveLicenses
    },
    getIsLoadingArchivedLicenses: ({ isLoadingArchivedLicenses }) => {
      return isLoadingArchivedLicenses
    },
    getIsLoadingArchivingLicenses: ({ isLoadingArchivingLicenses }) => {
      return isLoadingArchivingLicenses
    },
    getIsLoadingRestoringLicenses: ({ isLoadingRestoringLicenses }) => {
      return isLoadingRestoringLicenses
    },
    getIsLoadingEditingLicense: ({ isLoadingEditingLicense }) => {
      return isLoadingEditingLicense
    },
    getIsLoadingCreatingLicense: ({ isLoadingCreatingLicense }) => {
      return isLoadingCreatingLicense
    },
    getIsLoadingMessage: ({
      isLoadingArchivingLicenses,
      isLoadingRestoringLicenses,
      isLoadingEditingLicense,
      isLoadingCreatingLicense,
      isActionMultiple
    }) => {
      if (isLoadingArchivingLicenses || isLoadingRestoringLicenses)
        return isActionMultiple
          ? `${isLoadingArchivingLicenses ? 'Archiving' : 'Restoring'} institution licenses...`
          : `${isLoadingArchivingLicenses ? 'Archiving' : 'Restoring'} institution license...`
      else if (isLoadingEditingLicense) return 'Updating institution license...'
      else if (isLoadingCreatingLicense)
        return 'Creating institution license...'
      else return null
    },
    getErrors: ({
      errorActiveLicenses,
      errorArchivedLicenses,
      errorArchivingLicenses,
      errorRestoringLicenses,
      errorEditingLicense,
      errorCreatingLicense
    }) => {
      let returnValue = []
      if (errorActiveLicenses)
        returnValue.push({
          text: errorActiveLicenses,
          key: 'activeLicenses'
        })
      if (errorArchivedLicenses)
        returnValue.push({
          text: errorArchivedLicenses,
          key: 'archivedLicenses'
        })
      if (errorArchivingLicenses)
        returnValue.push({
          text: errorArchivingLicenses,
          key: 'archivingLicenses'
        })
      if (errorRestoringLicenses)
        returnValue.push({
          text: errorRestoringLicenses,
          key: 'restoringLicenses'
        })
      if (errorEditingLicense)
        returnValue.push({
          text: errorEditingLicense,
          key: 'editingLicense'
        })
      if (errorCreatingLicense)
        returnValue.push({
          text: errorCreatingLicense,
          key: 'creatingLicense'
        })
      return returnValue
    }
  },

  actions: {
    async fetchActiveLicenses(withLoader = true) {
      if (this.errorActiveLicenses != null) this.errorActiveLicenses = null
      this.statusActiveLicenses = null
      if (withLoader) this.isLoadingActiveLicenses = true
      try {
        const result = await licenseApi.get('', { params: { active: 1 } })
        const { error, institutionLicense } = result.data.response
        this.statusActiveLicenses = result.data.status
        if (error) this.errorActiveLicenses = error.message
        else this.activeLicenses = institutionLicense
      } catch (e) {
        this.errorActiveLicenses =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingActiveLicenses = false
    },
    async fetchArchivedLicenses(withLoader = true) {
      if (this.errorArchivedLicenses != null) this.errorArchivedLicenses = null
      this.statusArchivedLicenses = null
      if (withLoader) this.isLoadingArchivedLicenses = true
      try {
        const result = await licenseApi.get('', { params: { active: 0 } })
        const { error, institutionLicense } = result.data.response
        this.statusArchivedLicenses = result.data.status
        if (error) this.errorArchivedLicenses = error.message
        else this.archivedLicenses = institutionLicense
      } catch (e) {
        this.errorArchivedLicenses =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivedLicenses = false
    },
    _filterTableData() {
      if (this.selectedStatuses?.length == 0 && this.tableData?.length != 0)
        this.tableData = []
      else if (this.getData.data.length > 0)
        this.tableData = this.getData.data.filter(({ status }) =>
          this.selectedStatuses.includes(status)
        )
    },
    async archivingLicenses(licenses) {
      this.isActionMultiple = licenses?.length > 1
      this.errorArchivingLicenses = null
      this.isLoadingArchivingLicenses = true
      this.statusArchivingLicenses = null
      const licenseInstitution = _.mapValues(
        _.keyBy(licenses, 'id'),
        'institution'
      )
      const payload = {
        where: {
          id: {
            in: _.map(licenses, ({ id }) => id)
          }
        },
        data: {
          deleteTs: 'softDelete'
        }
      }
      try {
        const result = await licenseApi.patch('', payload)
        const { error, institutionLicense } = result.data.response
        this.statusArchivingLicenses = result.data.status
        if (error) this.errorArchivingLicenses = error.message
        else {
          this.archivedLicenses = [
            ...(this.archivedLicenses ?? []),
            ...institutionLicense.map((l) => {
              l.institution = licenseInstitution[l.id]
              return l
            })
          ]
          if (this.activeLicenses != null && this.activeLicenses.length)
            _.pullAt(this.activeLicenses, _.map(licenses, 'index'))
          this._filterTableData()
        }
      } catch (e) {
        this.errorArchivingLicenses =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivingLicenses = false
      this.isActionMultiple = null
    },
    async restoringLicenses(licenses) {
      this.isActionMultiple = licenses?.length > 1
      this.errorRestoringLicenses = null
      this.isLoadingRestoringLicenses = true
      this.statusRestoringLicenses = null
      const licenseInstitution = _.mapValues(
        _.keyBy(licenses, 'id'),
        'institution'
      )
      const payload = {
        where: {
          id: {
            in: _.map(licenses, ({ id }) => id)
          }
        },
        data: {
          deleteTs: 'restore'
        }
      }
      try {
        const result = await licenseApi.patch('', payload)
        const { error, institutionLicense } = result.data.response
        this.statusRestoringLicenses = result.data.status
        if (error) this.errorRestoringLicenses = error.message
        else {
          this.activeLicenses = [
            ...(this.activeLicenses ?? []),
            ...institutionLicense.map((l) => {
              l.institution = licenseInstitution[l.id]
              return l
            })
          ]
          if (this.archivedLicenses != null && this.archivedLicenses.length)
            _.pullAt(this.archivedLicenses, _.map(licenses, 'index'))
          this._filterTableData()
        }
      } catch (e) {
        this.errorRestoringLicenses =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingRestoringLicenses = false
      this.isActionMultiple = null
    },
    async editingLicense(license) {
      let {
        id,
        institution,
        validFrom,
        validTo,
        maxDeviceLicenses,
        deviceLicenseValidity,
        department,
        appMode,
        licensedUsers
      } = license
      if (department != null) department = department.replace(/^\s+|\s+$/g, '')
      if (appMode != null) appMode = appMode.replace(/^\s+|\s+$/g, '')

      this.errorEditingLicense = null
      this.isLoadingEditingLicense = true
      this.statusEditingLicense = null
      const payload = {
        where: { id },
        data: {}
      }
      if (validFrom != this.editLicense?.validFrom)
        payload.data.validFrom = validFrom
      if (validTo != this.editLicense?.validTo) payload.data.validTo = validTo
      if (maxDeviceLicenses != this.editLicense?.maxDeviceLicenses)
        payload.data.maxDeviceLicenses = maxDeviceLicenses
      const userCap = licensedUsers === '' || licensedUsers == null ? null : Number(licensedUsers)
      const currentCap = this.editLicense?.licensedUsers ?? null
      if (userCap !== currentCap) payload.data.licensedUsers = userCap
      if (deviceLicenseValidity != this.editLicense?.deviceLicenseValidity)
        payload.data.deviceLicenseValidity = deviceLicenseValidity
      if (department != this.editLicense?.department)
        payload.data.department = department
      if (appMode != this.editLicense?.appMode) payload.data.appMode = appMode
      try {
        const result = await licenseApi.patch('', payload)
        const { error, institutionLicense } = result.data.response
        this.statusEditingLicense = result.data.status
        if (error) this.errorEditingLicense = error.message
        else {
          this.activeLicenses[this.editLicense.index] = {
            ...institutionLicense[0],
            institution
          }
          this._filterTableData()
        }
      } catch (e) {
        this.errorEditingLicense =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingEditingLicense = false
    },
    async creatingLicense(license) {
      let {
        institution,
        validFrom,
        validTo,
        maxDeviceLicenses,
        department,
        appMode,
        licensedUsers
      } = license
      if (department != null) department = department.replace(/^\s+|\s+$/g, '')
      if (appMode != null) appMode = appMode.replace(/^\s+|\s+$/g, '')

      this.errorCreatingLicense = null
      this.isLoadingCreatingLicense = true
      this.statusCreatingLicense = null
      try {
        const params = { resource: 1 }
        const result = await institutionApi.put(
          `/${institution.id}/licenseCode`,
          {
            validFrom,
            validTo,
            maxDeviceLicenses,
            department,
            appMode,
            ...(licensedUsers === '' || licensedUsers == null
              ? {}
              : { licensedUsers: Number(licensedUsers) })
          },
          { params }
        )
        const { error, institutionLicense } = result.data.response
        this.statusCreatingLicense = result.data.status
        if (error) this.errorCreatingLicense = error.message
        else {
          this.activeLicenses.unshift({ ...institutionLicense, institution })
          this._filterTableData()
        }
      } catch (e) {
        this.errorCreatingLicense =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingCreatingLicense = false
    },
    setFetchActiveLicensesInterval(newValue) {
      if (newValue == null && this.fetchActiveLicensesInterval != null)
        clearInterval(this.fetchActiveLicensesInterval)
      this.fetchActiveLicensesInterval = newValue
    },
    setFetchArchivedLicensesInterval(newValue) {
      if (newValue == null && this.fetchArchivedLicensesInterval != null)
        clearInterval(this.fetchArchivedLicensesInterval)
      this.fetchArchivedLicensesInterval = newValue
    },
    setEditLicense(newValue) {
      this.editLicense = newValue
    },
    setTableData(newValue) {
      this.tableData = newValue
    },
    setCachedStatusSelection(newValue) {
      this.cachedStatusSelection = newValue
    },
    setSelectedStatuses(newValue) {
      this.selectedStatuses = newValue
    },
    initSelectedStatuses() {
      if (this.cachedStatusSelection == null)
        this.cachedStatusSelection =
          this.selectedStatuses == null ||
          this.selectedStatuses.includes('archived')
            ? Object.values(this.getData.statuses.active).map(
                ({ value }) => value
              )
            : [...this.selectedStatuses]
      if (this.selectedStatuses == null)
        this.selectedStatuses = [...this.cachedStatusSelection]
      this._filterTableData()
      if (
        this.selectedLicenseIds != null &&
        !_.isEmpty(this.selectedLicenseIds)
      )
        this.selectedLicenseIds = null
    },
    toggleSelectedStatus(newValue) {
      if (!this.isArchivedStatus) {
        const idx = this.cachedStatusSelection.indexOf(newValue)
        idx === -1
          ? this.cachedStatusSelection.push(newValue)
          : this.cachedStatusSelection.splice(idx, 1)

        this.selectedStatuses = [...this.cachedStatusSelection]
      }
      this._filterTableData()
      if (
        this.selectedLicenseIds != null &&
        !_.isEmpty(this.selectedLicenseIds)
      )
        this.selectedLicenseIds = null
    },
    toggleArchivedStatus() {
      if (this.isArchivedStatus) {
        this.selectedStatuses = [...this.cachedStatusSelection]
      } else {
        this.selectedStatuses = [this.getData.statuses.archived.value]
      }
      this._filterTableData()
      if (
        this.selectedLicenseIds != null &&
        !_.isEmpty(this.selectedLicenseIds)
      )
        this.selectedLicenseIds = null
    },
    resetSelectedStatuses() {
      this.cachedStatusSelection = Object.values(
        this.getData.statuses.active
      ).map(({ value }) => value)
      this.selectedStatuses = [...this.cachedStatusSelection]
      this._filterTableData()
      if (
        this.selectedLicenseIds != null &&
        !_.isEmpty(this.selectedLicenseIds)
      )
        this.selectedLicenseIds = null
    },
    setItemsPerPage(newValue) {
      this.itemsPerPage = newValue
      if (
        this.selectedLicenseIds != null &&
        !_.isEmpty(this.selectedLicenseIds)
      )
        this.selectedLicenseIds = null
    },
    cancelError(key) {
      switch (key) {
        case 'activeLicenses':
          this.errorActiveLicenses = null
          break
        case 'archivedLicenses':
          this.errorArchivedLicenses = null
          break
        case 'archivingLicenses':
          this.errorArchivingLicenses = null
          break
        case 'restoringLicenses':
          this.errorRestoringLicenses = null
          break
        case 'editingLicense':
          this.errorEditingLicense = null
          break
        case 'creatingLicense':
          this.errorCreatingLicense = null
          break
        default:
          break
      }
    }
  }
})
